import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { DocumentType, DOCUMENT_TYPE_LABELS } from "@/types/documento";
import { cn } from "@/lib/class-control";
import { ChevronDown, FileText, Folder, FileCheck, Sheet, X } from "lucide-react";

interface DocumentTypeFilterProps {
  value: DocumentType[];
  onChange: (value: DocumentType[]) => void;
  className?: string;
}

/**
 * DocumentTypeFilter Component
 *
 * Multi-select filter for document types using Popover + Checkbox pattern.
 * Shows active filter count badge and clear button.
 *
 * @param value - Array of selected document types
 * @param onChange - Callback when selection changes
 * @param className - Optional additional CSS classes
 */
export function DocumentTypeFilter({
  value,
  onChange,
  className,
}: DocumentTypeFilterProps) {
  const [open, setOpen] = useState(false);

  const handleToggle = (type: DocumentType) => {
    const newValue = value.includes(type)
      ? value.filter((t) => t !== type)
      : [...value, type];
    onChange(newValue);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  const getIcon = (type: DocumentType) => {
    switch (type) {
      case "contratos":
        return <FileText className="size-[16px]" />;
      case "projetos":
        return <Folder className="size-[16px]" />;
      case "notas-fiscais":
        return <FileCheck className="size-[16px]" />;
      case "planilhas":
        return <Sheet className="size-[16px]" />;
    }
  };

  const activeCount = value.length;
  const hasActiveFilters = activeCount > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "border border-[#5D5D5D] rounded-[10px] px-[20px] py-[10px] h-[50px] min-w-[200px]",
            "bg-transparent text-[#F6F6F6] text-[16px] font-['Plus_Jakarta_Sans']",
            "hover:bg-[#15203C]/50 transition-colors",
            "flex items-center justify-between gap-[10px]",
            className
          )}
        >
          <span className="flex items-center gap-[8px]">
            <span>Tipo de Documento</span>
            {hasActiveFilters && (
              <span className="inline-flex items-center justify-center size-[20px] rounded-full bg-[#7846FF] text-white text-[12px] font-bold">
                {activeCount}
              </span>
            )}
          </span>
          <div className="flex items-center gap-[8px]">
            {hasActiveFilters && (
              <button
                onClick={handleClear}
                className="hover:bg-white/10 rounded-full p-[2px] transition-colors"
                aria-label="Limpar filtros"
              >
                <X className="size-[16px]" />
              </button>
            )}
            <ChevronDown
              className={cn(
                "size-[16px] transition-transform",
                open && "rotate-180"
              )}
            />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[280px] bg-[#15203C] border-[#5D5D5D] p-[16px]"
      >
        <div className="flex flex-col gap-[12px]">
          <div className="flex items-center justify-between mb-[8px]">
            <h3 className="text-[14px] font-semibold text-white font-['Plus_Jakarta_Sans']">
              Tipo de Documento
            </h3>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="h-auto p-0 text-[12px] text-[#7846FF] hover:text-[#7846FF]/80 hover:bg-transparent"
              >
                Limpar tudo
              </Button>
            )}
          </div>
          {(Object.keys(DOCUMENT_TYPE_LABELS) as DocumentType[]).map((type) => (
            <div
              key={type}
              className="flex items-center gap-[12px] hover:bg-white/5 p-[8px] rounded-[6px] transition-colors cursor-pointer"
              onClick={() => handleToggle(type)}
            >
              <div className="text-[#F6F6F6]">{getIcon(type)}</div>
              <Checkbox
                id={`doc-type-${type}`}
                checked={value.includes(type)}
                onCheckedChange={() => handleToggle(type)}
                className="pointer-events-none"
              />
              <label
                htmlFor={`doc-type-${type}`}
                className="text-[14px] text-[#F6F6F6] font-['Plus_Jakarta_Sans'] flex-1 cursor-pointer"
              >
                {DOCUMENT_TYPE_LABELS[type]}
              </label>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
